import { datePickerFormItemProps, datePickerStandardProps } from "@/utils/componentUtils";
import { Alert, Col, DatePicker, Form, Modal, Row } from "antd";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useAssignCommissionsSettingsLevel } from "../../../../../common/api/queries";
import HiddenInput from "../../../../../common/components/form/components/HiddenInput";
import LabelWithTooltip from "../../../../../common/components/form/labels/LabelWithTooltip";
import { ModalSizes, rowGutter } from "../../../../../common/constants";
import { setErrorsToForm } from "../../../../../common/utils/formUtils";
import { validations } from "../../../../../common/utils/validationUtils";
import { AgentSelect } from "../../../../enumerations/components/form/AgentSelect";
import CommissionsLevelSelect from "../../../../enumerations/components/form/CommissionsLevelSelect";
import { AssignCommissionsSettingsLevel, CommissionsSettings } from "../../types";

interface Props {
  isTopAgentTypePlus: boolean;
  open: boolean;
  settings: CommissionsSettings;
  onFormCancel: VoidFunction;
}

export const CommissionsSettingsAssignLevelForm = ({ isTopAgentTypePlus, open, settings, onFormCancel }: Props) => {
  const { t } = useTranslation();

  const [form] = Form.useForm<AssignCommissionsSettingsLevel>();

  const assignCommissionsSettingsLevel = useAssignCommissionsSettingsLevel();

  useEffect(() => {
    if (open && settings) {
      form.setFieldsValue({ levelId: settings.level?.id, optimisticLockVersion: settings.optimisticLockVersion });
    }
  }, [form, open, settings]);

  const handleFormSubmit = () => {
    form.validateFields().then((values) =>
      assignCommissionsSettingsLevel.mutate(
        {
          id: settings.agent.id,
          object: values,
        },
        {
          onSuccess: onFormCancel,
          onError: (error) => setErrorsToForm(form, "commissions.settings.attrs", error.violations),
        },
      ),
    );
  };

  return (
    <Modal
      width={ModalSizes.MEDIUM}
      open={open}
      title={t("commissions.settings.titles.agentLevelAssign")}
      okText={t("common.save")}
      cancelText={t("common.cancel")}
      confirmLoading={assignCommissionsSettingsLevel.isPending}
      afterClose={() => form.resetFields()}
      onOk={handleFormSubmit}
      onCancel={onFormCancel}
      mask={{ closable: false }}
    >
      <Form form={form} layout="vertical" name="commissionsSettingsAssignLevelForm">
        <HiddenInput name="optimisticLockVersion" />

        <Row gutter={rowGutter}>
          <Col span={10}>
            <CommissionsLevelSelect
              formItemProps={{
                name: "levelId",
                label: t("commissions.settings.attrs.levelId"),
                rules: [validations.notNull],
              }}
            />
          </Col>

          {isTopAgentTypePlus ? undefined : (
            <Col span={7}>
              <Form.Item
                name="levelSuperiorAgentId"
                label={
                  <LabelWithTooltip
                    label={t("commissions.settings.attrs.levelSuperiorAgentId")}
                    tooltip={t("commissions.settings.helpers.assignLevelSuperiorAgentDesc")}
                  />
                }
                rules={[validations.notNull]}
              >
                <AgentSelect
                  optionsProps={{ superiorTreePath: settings.agent.accessTreePath, excludedIds: [settings.agent.id] }}
                />
              </Form.Item>
            </Col>
          )}

          <Col span={7}>
            <Form.Item
              name="levelChangeDate"
              label={t("commissions.settings.attrs.levelChangeDate")}
              rules={[validations.notNull]}
              {...datePickerFormItemProps}
            >
              <DatePicker {...datePickerStandardProps} />
            </Form.Item>
          </Col>
        </Row>
      </Form>

      <Alert type="info" title={t("commissions.settings.helpers.assignLevelDesc")} showIcon />
    </Modal>
  );
};
